import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';

import { withAccount } from '../../components/AccountContext';
import { withGrowl } from '../../components/GrowlNotifications';
import { intlShape, historyShape, growlShape } from '../../components/prop-types';
import PageNotFound from '../../components/PageNotFound';
import { Loading } from '../../components/Loading';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { InputTextWithValidationMessage } from '../../components/prime-formik';

const messages = defineMessages({
  name: { id: 'attrs.name', defaultMessage: 'Name' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  addSubAccount: {
    id: 'accounts.add-sub-account.heading',
    defaultMessage: 'Add Sub-account',
  },
});

const CREATE_SUB_ACCOUNT = gql`
  mutation createSubAccount($input: CreateSubAccountInput!) {
    createSubAccount(input: $input) {
      clientMutationId
    }
  }
`;

const PARENT_ACCOUNT_QUERY = gql`
  query querySingleAccountById($accountId: ID!) {
    account(id: $accountId) {
      id
      displayName
    }
  }
`;

function AddSubAccountForm(props) {
  const {
    history,
    currentAccount,
    intl: { formatMessage },
    isSubmitting,
  } = props;

  return (
    <Form>
      <input type="hidden" name="account" value={currentAccount} />
      <div className="p-grid p-fluid">
        <div className="p-col-12">
          <Field
            id="displayName"
            name="displayName"
            type="text"
            labelText={formatMessage(messages.name)}
            component={InputTextWithValidationMessage}
          />
        </div>
      </div>
      <div className="p-grid p-nogutter">
        <div className="p-col-12">
          <Button
            type="button"
            className="p-button-secondary"
            label={formatMessage(messages.cancel)}
            onClick={() => history.push('/')}
          />
          <Button type="submit" label={formatMessage(messages.submit)} disabled={isSubmitting} />
        </div>
      </div>
      <Debug />
    </Form>
  );
}

AddSubAccountForm.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    createSubAccountMutation,
    growl,
    history,
    intl: { formatMessage },
  } = props;
  const { account, displayName } = values;
  try {
    setSubmitting(true);
    await createSubAccountMutation({
      variables: {
        input: {
          account,
          displayName,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success) });
    history.push('/');
  } catch (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }
}

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    displayName: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
  });
}

function mapPropsToValues({ intl: { formatMessage }, currentAccount }) {
  // Formik requires initial values for each field, to support error messages on submit
  const validationSchema = buildValidationSchema(formatMessage);

  const initialValues = {
    ...validationSchema.cast(),
    account: currentAccount,
    displayName: '',
  };
  return initialValues;
}

const AddSubAccountEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(AddSubAccountForm);

function AddSubAccount(props) {
  const {
    intl: { formatMessage },
    growl,
    currentAccount,
  } = props;

  const { loading, error, data } = useQuery(PARENT_ACCOUNT_QUERY, {
    variables: {
      accountId: currentAccount,
    },
  });

  const [createSubAccountMutation] = useMutation(CREATE_SUB_ACCOUNT, {
    refetchQueries: ['getViewerAccounts'],
  });

  if (error) {
    growl.show({ severity: 'error', detail: error.message, sticky: true });
  }

  if (loading) {
    return <Loading />;
  }

  if (data == null && data.account == null) {
    return <PageNotFound />;
  }

  const { displayName } = data.account;

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="p-grid">
            <div className="p-col-12 p-md-6">
              <Panel header={formatMessage(messages.addSubAccount)}>
                <div className="p-grid">
                  <div className="p-col-12">
                    <p>
                      <FormattedMessage
                        id="accounts.add-sub-account.instructions.01"
                        defaultMessage={'Create an account within the {accountName} Family'}
                        values={{
                          accountName: <strong>{displayName}</strong>,
                        }}
                      />
                    </p>
                    <AddSubAccountEnhancedForm createSubAccountMutation={createSubAccountMutation} {...props} />
                  </div>
                </div>
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AddSubAccount.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
};

export default withAccount(injectIntl(withRouter(withGrowl(AddSubAccount))));
